// Mirrors backend/app/core/vocab.py — single source of truth for the
// controlled vocabularies both wizards use. Keep these two files in sync.

export const CLINICAL_CONCERNS: { value: string; label: string }[] = [
  { value: 'anxiety', label: 'Anxiety' },
  { value: 'depression', label: 'Depression' },
  { value: 'trauma_ptsd', label: 'Trauma / PTSD' },
  { value: 'relationship_issues', label: 'Relationship Issues' },
  { value: 'couples_therapy', label: 'Couples Therapy' },
  { value: 'marriage_counseling', label: 'Marriage Counseling' },
  { value: 'family_conflict', label: 'Family Conflict' },
  { value: 'parenting', label: 'Parenting' },
  { value: 'grief_loss', label: 'Grief & Loss' },
  { value: 'stress', label: 'Stress' },
  { value: 'burnout', label: 'Burnout' },
  { value: 'workplace_stress', label: 'Workplace Stress' },
  { value: 'life_transitions', label: 'Life Transitions' },
  { value: 'adhd', label: 'ADHD' },
  { value: 'autism', label: 'Autism' },
  { value: 'ocd', label: 'OCD' },
  { value: 'bipolar_disorder', label: 'Bipolar Disorder' },
  { value: 'substance_use', label: 'Substance Use' },
  { value: 'eating_disorders', label: 'Eating Disorders' },
  { value: 'anger', label: 'Anger' },
  { value: 'self_esteem', label: 'Self-esteem' },
  { value: 'sleep_issues', label: 'Sleep Issues' },
  { value: 'chronic_illness', label: 'Chronic Illness' },
  { value: 'lgbtq_support', label: 'LGBTQ+ Support' },
  { value: 'mens_issues', label: "Men's Issues" },
  { value: 'womens_issues', label: "Women's Issues" },
  { value: 'teen_support', label: 'Teen Support' },
  { value: 'child_therapy', label: 'Child Therapy' },
  { value: 'pregnancy_postpartum', label: 'Pregnancy/Postpartum' },
  { value: 'faith_based_counseling', label: 'Faith-based Counseling' },
  { value: 'other', label: 'Other' },
]

export const THERAPY_MODALITIES: { value: string; label: string }[] = [
  { value: 'cbt', label: 'CBT' },
  { value: 'dbt', label: 'DBT' },
  { value: 'act', label: 'ACT' },
  { value: 'emdr', label: 'EMDR' },
  { value: 'psychodynamic', label: 'Psychodynamic' },
  { value: 'solution_focused', label: 'Solution Focused' },
  { value: 'narrative_therapy', label: 'Narrative Therapy' },
  { value: 'mindfulness', label: 'Mindfulness' },
  { value: 'christian_counseling', label: 'Christian Counseling' },
  { value: 'no_preference', label: 'No Preference' },
]

export const GENDER_PREFERENCES: { value: string; label: string }[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'no_preference', label: 'No Preference' },
]

// Patient-facing "Who is seeking care?" intake question (preferences.age_group).
export const AGE_GROUPS: { value: string; label: string }[] = [
  { value: 'adult', label: 'Myself / Adult' },
  { value: 'teen', label: 'Teen (13–17)' },
  { value: 'child', label: 'Child (0–12)' },
]

export const SELF_PAY_BUDGET_RANGES: { value: string; label: string }[] = [
  { value: 'under_100', label: 'Under $100' },
  { value: '100_150', label: '$100–150' },
  { value: '150_200', label: '$150–200' },
  { value: '200_plus', label: '$200+' },
]

export const WEEKDAYS: { value: string; label: string }[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
]

export const TIME_WINDOWS: { value: string; label: string }[] = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
]

export const INSURANCE_TYPES: { value: string; label: string }[] = [
  { value: 'private_insurance', label: 'Private Insurance' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'eap', label: 'EAP' },
  { value: 'self_pay', label: 'Self-pay' },
]

export const INSURANCE_CARRIERS: { value: string; label: string }[] = [
  { value: 'aetna', label: 'Aetna' },
  { value: 'blue_cross_blue_shield', label: 'Blue Cross Blue Shield' },
  { value: 'cigna', label: 'Cigna' },
  { value: 'unitedhealthcare', label: 'UnitedHealthcare' },
  { value: 'kaiser', label: 'Kaiser' },
  { value: 'humana', label: 'Humana' },
  { value: 'other', label: 'Other' },
]

export const ACCEPTING_NEW_INSURANCE_PATIENTS: { value: string; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'no', label: 'No' },
]

export const SERVICE_TYPES: { value: string; label: string }[] = [
  { value: 'individual', label: 'Individual therapy' },
  { value: 'couples', label: 'Couples therapy' },
  { value: 'family', label: 'Family therapy' },
  { value: 'group', label: 'Group therapy' },
  { value: 'medication_management', label: 'Medication management' },
  { value: 'assessment_only', label: 'Assessment only' },
]

// Display-only labels for Provider.credentialing_status — the stored value
// (backend/app/core/vocab.py CREDENTIALING_STATUSES) never changes; this is
// purely how CredentialingBadge renders it for staff-facing views.
export const CREDENTIALING_STATUS_LABELS: Record<string, string> = {
  unverified: 'Draft',
  submitted: 'Submitted',
  in_review: 'Pending Verification',
  approved: 'Verified',
  action_required: 'Action Required',
  rejected: 'Rejected',
}

// Mirrors backend/app/core/vocab.py's CALENDAR_CONNECTOR_TYPES/
// IMPLEMENTED_CALENDAR_CONNECTORS — only "ics" is real; the rest render as
// disabled "Coming Soon" options in the onboarding wizard's Connect Calendar step.
export const CALENDAR_CONNECTOR_TYPES = [
  'ics', 'google', 'microsoft', 'apple', 'calendly',
  'jane_app', 'simplepractice', 'therapynotes', 'sessions_health',
]
export const IMPLEMENTED_CALENDAR_CONNECTORS = new Set(['ics'])

export const CALENDAR_CONNECTOR_LABELS: Record<string, string> = {
  ics: 'Generic ICS / iCal',
  google: 'Google Calendar',
  microsoft: 'Microsoft Outlook',
  apple: 'Apple Calendar',
  calendly: 'Calendly',
  jane_app: 'Jane App',
  simplepractice: 'SimplePractice',
  therapynotes: 'TherapyNotes',
  sessions_health: 'Sessions Health',
}
